/**
 * Pure list logic behind `ScopedListManager` (Brand Knowledge list fields) and the
 * `itemTargets` reconciliation of `useStrategyTargets`.
 *
 * Two keys, two rules:
 *  - a SCOPE is keyed by the item's EXACT text (`itemTargets[field][itemText]`, the API
 *    contract), so every operation that changes a text must move its scope entry with it;
 *  - ADDING dedupes case-insensitively (after whitespace normalisation), so a paste of
 *    « Livraison rapide » next to an existing « livraison rapide » is reported as a
 *    duplicate instead of becoming a second, near-identical item.
 *
 * Nothing here mutates its inputs.
 */
import type { MarqueProfile, StrategyItemTargets, StrategyScopedList } from '@traaker/shared';

export type Scopes = Record<string, string>;

/** Filter sentinels. A real filter value is a Cibles targetId (a uuid). */
export const SCOPE_FILTER_ALL = '__all__';
export const SCOPE_FILTER_GLOBAL = '__global__';
export type ScopeFilter = string;

/** Trim + collapse inner whitespace runs (a pasted line often carries tabs or double spaces). */
export function normalizeItem(raw: string): string {
  return raw.replace(/\s+/g, ' ').trim();
}

/** Comparison key for dedupe: normalised, lowercased. */
function dedupeKey(text: string): string {
  return normalizeItem(text).toLocaleLowerCase();
}

/** Accent + case fold for the search box. */
export function foldText(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
}

/** Leading list markers a paste from a doc or a slide carries: « - », « • », « * », « 1. », « 2) ». */
const BULLET_RE = /^(?:[-*•·–—]\s+|\d{1,3}[.)]\s+)/;

export interface PasteResult {
  /** Items to append, in paste order, already normalised. */
  added: string[];
  skippedDuplicate: number;
  skippedTooLong: number;
  /** Valid, new items that did not fit under `maxItems`. */
  skippedOverLimit: number;
}

/**
 * Split a typed or pasted value into new items. One item per line; blank lines and bare
 * bullets are ignored (not counted). A line longer than `maxCharsPerItem` is SKIPPED and
 * counted, never truncated: a silently shortened criterion is a different criterion.
 */
export function parsePastedItems(
  raw: string,
  existing: readonly string[],
  limits: { maxItems: number; maxCharsPerItem: number },
): PasteResult {
  const seen = new Set(existing.map(dedupeKey));
  const room = Math.max(0, limits.maxItems - existing.length);
  const result: PasteResult = { added: [], skippedDuplicate: 0, skippedTooLong: 0, skippedOverLimit: 0 };
  for (const line of raw.split(/\r\n|\r|\n/)) {
    const text = normalizeItem(normalizeItem(line).replace(BULLET_RE, ''));
    if (!text) continue;
    if (text.length > limits.maxCharsPerItem) { result.skippedTooLong++; continue; }
    const key = dedupeKey(text);
    if (seen.has(key)) { result.skippedDuplicate++; continue; }
    if (result.added.length >= room) { result.skippedOverLimit++; continue; }
    seen.add(key);
    result.added.push(text);
  }
  return result;
}

/** Append `added` and give each the scope `targetId` (none when undefined). */
export function appendItems(
  items: readonly string[],
  scopes: Scopes,
  added: readonly string[],
  targetId?: string,
): { items: string[]; scopes: Scopes } {
  const nextScopes = { ...scopes };
  if (targetId) for (const text of added) nextScopes[text] = targetId;
  return { items: [...items, ...added], scopes: nextScopes };
}

export type RenameResult =
  | { ok: true; items: string[]; scopes: Scopes; changed: boolean }
  | { ok: false; reason: 'empty' | 'duplicate' | 'tooLong' };

/**
 * Rename one item in place and RE-KEY its scope onto the new text. A case-only change
 * of the same item is allowed; colliding with ANOTHER item (case-insensitively) is not.
 */
export function renameItem(
  items: readonly string[],
  scopes: Scopes,
  from: string,
  to: string,
  maxCharsPerItem: number,
): RenameResult {
  const text = normalizeItem(to);
  if (!text) return { ok: false, reason: 'empty' };
  if (text.length > maxCharsPerItem) return { ok: false, reason: 'tooLong' };
  const idx = items.indexOf(from);
  if (idx < 0 || text === from) return { ok: true, items: [...items], scopes: { ...scopes }, changed: false };
  const key = dedupeKey(text);
  if (items.some((it, i) => i !== idx && dedupeKey(it) === key)) return { ok: false, reason: 'duplicate' };
  const nextItems = [...items];
  nextItems[idx] = text;
  const nextScopes = { ...scopes };
  if (from in nextScopes) {
    nextScopes[text] = nextScopes[from];
    delete nextScopes[from];
  }
  return { ok: true, items: nextItems, scopes: nextScopes, changed: true };
}

/** Remove items and their scope entries. */
export function removeItems(
  items: readonly string[],
  scopes: Scopes,
  toRemove: Iterable<string>,
): { items: string[]; scopes: Scopes } {
  const drop = new Set(toRemove);
  const nextScopes: Scopes = {};
  for (const [text, id] of Object.entries(scopes)) if (!drop.has(text)) nextScopes[text] = id;
  return { items: items.filter((it) => !drop.has(it)), scopes: nextScopes };
}

/** Set (or clear, with `undefined`) the scope of several items at once. */
export function assignScope(scopes: Scopes, texts: Iterable<string>, targetId: string | undefined): Scopes {
  const next = { ...scopes };
  for (const text of texts) {
    if (targetId) next[text] = targetId;
    else delete next[text];
  }
  return next;
}

/**
 * The scopes as the UI must SHOW them: entries whose id does not resolve (a deleted
 * cible, or one we cannot name) are dropped, so the item reads, filters and counts as
 * « Toutes les cibles » — exactly how every reader of `itemTargets` treats it. The
 * stored map is untouched; only `reconcileItemTargets` removes entries, and only the
 * confirmed-missing ones.
 */
export function effectiveScopes(scopes: Scopes, isResolvable: (targetId: string) => boolean): Scopes {
  const out: Scopes = {};
  for (const [text, id] of Object.entries(scopes)) if (id && isResolvable(id)) out[text] = id;
  return out;
}

/** Items matching the scope filter AND the (accent/case-insensitive) search query, in list order. */
export function filterByScope(
  items: readonly string[],
  scopes: Scopes,
  filter: ScopeFilter,
  query = '',
): string[] {
  const q = foldText(query.trim());
  return items.filter((text) => {
    if (filter === SCOPE_FILTER_GLOBAL && scopes[text]) return false;
    if (filter !== SCOPE_FILTER_ALL && filter !== SCOPE_FILTER_GLOBAL && scopes[text] !== filter) return false;
    return !q || foldText(text).includes(q);
  });
}

/** Per-scope item counts, for the filter pills. `byTarget` is sorted by count desc. */
export function scopeCounts(
  items: readonly string[],
  scopes: Scopes,
): { global: number; byTarget: Array<[targetId: string, count: number]> } {
  const counts = new Map<string, number>();
  let global = 0;
  for (const text of items) {
    const id = scopes[text];
    if (!id) global++;
    else counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  return { global, byTarget: [...counts.entries()].sort((a, b) => b[1] - a[1]) };
}

/**
 * Prepare `itemTargets` for a save. Drops an entry when its id is CONFIRMED missing
 * (`missingIds`, fed only by the by-id lookup's `notFound`) or when its item text is no
 * longer in the field's list. An absent `lists[field]` means the field has no items.
 * An id merely UNRESOLVED (lookup failed, still loading) is kept: an outage must never
 * wipe a user's mappings. Returns `undefined` when nothing is left.
 */
export function reconcileItemTargets(
  it: StrategyItemTargets | undefined,
  lists: Partial<Record<StrategyScopedList, string[]>>,
  missingIds: ReadonlySet<string>,
): StrategyItemTargets | undefined {
  if (!it) return undefined;
  const out: StrategyItemTargets = {};
  for (const [field, map] of Object.entries(it) as Array<[StrategyScopedList, Record<string, string> | undefined]>) {
    if (!map) continue;
    const present = new Set(lists[field] ?? []);
    const kept: Record<string, string> = {};
    for (const [text, id] of Object.entries(map)) {
      if (id && present.has(text) && !missingIds.has(id)) kept[text] = id;
    }
    if (Object.keys(kept).length) out[field] = kept;
  }
  return Object.keys(out).length ? out : undefined;
}

/** A profile with every field blank says nothing: the marque inherits everything. */
function cleanProfile(p: MarqueProfile | undefined): MarqueProfile | undefined {
  if (!p) return undefined;
  const out: MarqueProfile = {};
  if (p.clientPersonas?.trim()) out.clientPersonas = p.clientPersonas;
  if (p.businessModel) out.businessModel = p.businessModel;
  if (p.geographicFocus?.trim()) out.geographicFocus = p.geographicFocus;
  return Object.keys(out).length ? out : undefined;
}

/**
 * Prepare `marqueProfiles` for a save, with the same fail-closed rule as
 * `reconcileItemTargets`: a profile is dropped when it is empty or when its marque id
 * is CONFIRMED missing (`missingIds`). An id merely unresolved is kept. Blank fields are
 * stripped. Returns `undefined` when nothing is left.
 */
export function reconcileMarqueProfiles(
  profiles: Record<string, MarqueProfile> | undefined,
  missingIds: ReadonlySet<string>,
): Record<string, MarqueProfile> | undefined {
  if (!profiles) return undefined;
  const out: Record<string, MarqueProfile> = {};
  for (const [id, p] of Object.entries(profiles)) {
    const kept = cleanProfile(p);
    if (id && kept && !missingIds.has(id)) out[id] = kept;
  }
  return Object.keys(out).length ? out : undefined;
}

/**
 * The ids to send to `POST /brands/:id/knowledge/reconcile` on display: CONFIRMED missing
 * (`missingIds`) AND still scoping at least one stored item. Sorted, deduped, chunked to
 * `chunk` ids per call. A `marqueProfiles` key that is missing is not an item scope and
 * is left to the save path.
 */
export function idsToReconcile(
  missingIds: ReadonlySet<string>,
  scopes: StrategyItemTargets | undefined,
  chunk: number,
): string[][] {
  const ids = new Set<string>();
  for (const map of Object.values(scopes ?? {})) {
    for (const id of Object.values(map ?? {})) if (id && missingIds.has(id)) ids.add(id);
  }
  const sorted = [...ids].sort();
  const out: string[][] = [];
  for (let i = 0; i < sorted.length; i += chunk) out.push(sorted.slice(i, i + chunk));
  return out;
}

/**
 * The `itemTargets` value of a strategy PATCH. The API treats `itemTargets` as the FULL
 * desired scoping, so it is sent (`{}` when nothing is left) only when the stored scopes
 * were LOADED: a draft built on scopes that never arrived would unassign every item.
 * When they were not loaded the key is omitted (`undefined`) and the server leaves the
 * scoping rows as they are, pruning only the texts the save removed.
 */
export function itemTargetsForSave(
  scopesLoaded: boolean,
  reconciled: StrategyItemTargets | undefined,
): StrategyItemTargets | undefined {
  return scopesLoaded ? reconciled ?? {} : undefined;
}
